/**
 * Inicio de un comité SIN Facturación contratada (plan parcial: sólo
 * Asambleas, sólo Operaciones, etc.). El dashboard operativo gira en torno al
 * ciclo de lectura → boleta → cobranza y le mostraría KPIs vacíos; acá van
 * accesos directos a lo que el comité sí contrató.
 */
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  ClipboardList,
  Gauge,
  Headphones,
  Package,
  ShieldAlert,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import { hasPermission } from "../../lib/auth";
import { tieneModulo, type ModuloCode } from "../../lib/modulos";
import { InicioContablePage } from "../contabilidad/InicioContablePage";
import { useAuth } from "../auth/useAuth";

interface Acceso {
  modulo: ModuloCode;
  titulo: string;
  descripcion: string;
  to: string;
  permiso: string;
  icon: LucideIcon;
}

const ACCESOS: readonly Acceso[] = [
  {
    modulo: "asambleas",
    titulo: "Asambleas",
    descripcion: "Reuniones, asistencia y multas por inasistencia.",
    to: "/asambleas",
    permiso: "asambleas.read",
    icon: ClipboardList,
  },
  {
    modulo: "padron",
    titulo: "Padrón de socios",
    descripcion: "Socios, sectores y arranques del comité.",
    to: "/socios",
    permiso: "socios.read",
    icon: Users,
  },
  {
    modulo: "lecturas",
    titulo: "Lecturas",
    descripcion: "Lecturas tomadas en terreno.",
    to: "/lecturas",
    permiso: "lecturas.read",
    icon: Gauge,
  },
  {
    modulo: "atencion",
    titulo: "Atención al usuario",
    descripcion: "Reclamos, consultas y solicitudes con sus plazos.",
    to: "/atenciones",
    permiso: "atenciones.read",
    icon: Headphones,
  },
  {
    modulo: "continuidad",
    titulo: "Continuidad y planta",
    descripcion: "Interrupciones, obstrucciones, presiones y control de desinfección.",
    to: "/continuidad",
    permiso: "continuidad.read",
    icon: ShieldAlert,
  },
  {
    modulo: "operaciones",
    titulo: "Bodega y compras",
    descripcion: "Inventario de insumos, proveedores y compras.",
    to: "/bodega",
    permiso: "inventario.read",
    icon: Package,
  },
  {
    modulo: "operaciones",
    titulo: "Mantenimiento",
    descripcion: "Activos, órdenes de trabajo y planes de mantención.",
    to: "/ordenes-trabajo",
    permiso: "mantenimiento.read",
    icon: Wrench,
  },
  {
    modulo: "contabilidad",
    titulo: "Contabilidad",
    descripcion: "Indicadores, balance de 8 columnas, libro mayor y libro diario.",
    to: "/contabilidad",
    permiso: "contabilidad.read",
    icon: BookOpen,
  },
];

export function InicioModulosPage(): JSX.Element {
  const { state } = useAuth();
  const accesos = ACCESOS.filter(
    (a) => tieneModulo(state?.modulos, a.modulo) && hasPermission(state, a.permiso),
  );
  const comite = state?.empresaTargetNombre ?? state?.empresaNombre;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold text-neutral-900">
          {comite ? `Bienvenido a ${comite}` : "Bienvenido"}
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          Estos son los módulos que tu comité tiene contratados.
        </p>
      </div>

      {accesos.length === 0 ? (
        <div className="rounded-lg border border-neutral-200 bg-white p-6 text-sm text-neutral-600">
          Tu usuario todavía no tiene permisos sobre los módulos del comité. Pide
          acceso al administrador.
        </div>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {accesos.map((a) => (
            <li key={a.to}>
              <Link
                to={a.to}
                className="group flex h-full items-start gap-3 rounded-lg border border-neutral-200 bg-white p-4 transition-colors hover:border-brand-300 hover:bg-brand-50/40"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-brand-100 text-brand-800">
                  <a.icon className="h-5 w-5" strokeWidth={1.6} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-neutral-900">{a.titulo}</span>
                  <span className="block text-sm text-neutral-600">{a.descripcion}</span>
                </span>
                <ArrowRight className="mt-1 h-4 w-4 text-neutral-500 transition-transform group-hover:translate-x-0.5 group-hover:text-brand-700" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** "/" del admin: el dashboard operativo si hay Facturación, si no el Inicio por módulos. */
export function InicioPage({ dashboard }: { dashboard: JSX.Element }): JSX.Element {
  const { state } = useAuth();
  // La directiva (sólo contabilidad.read) o un comité que contrató sólo
  // Contabilidad aterrizan en el resumen contable: el dashboard operativo
  // pide permisos de padrón y facturación que no tienen.
  const veFacturacion =
    tieneModulo(state?.modulos, "facturacion") && hasPermission(state, "socios.read");
  const veContabilidad =
    tieneModulo(state?.modulos, "contabilidad") && hasPermission(state, "contabilidad.read");
  if (veFacturacion) return dashboard;
  return veContabilidad ? <InicioContablePage /> : <InicioModulosPage />;
}
